import AdminOnlyRoute from './components/AdminOnlyRoute';
import './App.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './utils/PrivateRoute';
import PluginGuard from './components/PluginGuard';
import { AuthProvider } from './context/AuthContext';
import { PluginProvider } from './context/PluginContext';
import { SidebarBadgesProvider } from './context/SidebarBadgesContext';
import { hasCatalogSchema, getDashboardWidgets, canUseTools, configVersion, subscribeBusinessConfig } from './utils/businessConfig';
// Path list only (a small array of strings) — imported eagerly so the routes
// exist on first render. The PAGE component behind them is still lazy.
import { SEO_PATHS } from './pages/seo/seoPages';

// Light components — load eagerly (small, essential for first render)
import Login from './pages/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Base from './components/Base';
import AdminRoute from './components/admin/AdminRoute';
import ImpersonationBanner from './components/ImpersonationBanner';

// Medium components — lazy but preloaded
// SignupJourney is the one-page delivery-journey signup (shelf → packing →
// address → lock → delivery → opening). The classic wizard stays mounted at
// /register-classic as its escape hatch and data-load fallback — the journey
// redirects there if /api/signup/setup/ never answers.
const SignupJourney = lazy(() => import('./pages/SignupJourney'));
const Signup = lazy(() => import('./pages/Signup'));
const Setup = lazy(() => import('./pages/Setup'));
const LandingPage = lazy(() => import('./pages/landing_page'));
const ReturnsPage = lazy(() => import('./pages/Returns'));
const SeoLandingPage = lazy(() => import('./pages/seo/SeoLandingPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const ErpDashboard = lazy(() => import('./pages/Dashboard/ErpDashboard'));

// NOTE: We intentionally do NOT eagerly prefetch the TemplateThemes / VisualTemplateEditor
// chunks. TemplateThemes is a very large chunk; prefetching it for every visitor (even those
// who never open it) hurt initial load. These routes are React.lazy()-loaded on demand below,
// which is fast enough and avoids downloading a big chunk users may never need.

// Heavy components — load lazily (only when route is visited)
const ContactUs = lazy(() => import('./pages/ContactUs'));
const TermsAndServices = lazy(() => import('./components/TermsNcondn'));
const ReturnPolicy = lazy(() => import('./components/ReturnPolicy'));
const PaymentShipping = lazy(() => import('./components/PaymentShipping'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Faqs = lazy(() => import('./components/Faqs'));
const Uploadproduct = lazy(() => import('./pages/Uploadproduct'));
const SchemaCatalog = lazy(() => import('./pages/Catalog/SchemaCatalog'));
const SchemaProductForm = lazy(() => import('./pages/Catalog/SchemaProductForm'));

// ONE upload route, `/UploadProduct`, for every business — every entry point in
// the app already points at it (Sidebar, MobileBottomNav, Categories, the
// dashboards, Insights). What renders inside it is decided by whether this
// business has a catalog schema:
//
//   schema  → SchemaProductForm, whose middle section IS that schema, resolved
//             per PRODUCT TYPE (`/api/catalog/schema/?product_type=`) — the
//             standard taxonomy the merchant picks from, NOT their own category
//             names. Those two were one field once, which meant guessing a type
//             from a shop-menu label; see the note on Product.category.
//   empty   → Uploadproduct, the bespoke garment form. Fashion's schema is empty
//             on purpose (business_presets.product_type_schema: "empty in,
//             empty out"), and that emptiness is what keeps it here.
//
// Both now share the same photo picker, the same progressive-disclosure shell,
// the same server-built CSV importer and the same duplicate action — the schema
// decides the FIELDS, never the ceiling.
const CatalogEntry = () => (
  hasCatalogSchema() ? <SchemaProductForm/> : <Uploadproduct/>
);

// Home dashboard. Businesses whose blueprint declares dashboard widgets (pure
// ERP verticals) get the native ERP dashboard; fashion (no widgets) keeps its
// storefront HomePage. getDashboardWidgets() is empty for every existing user.
const HomeDashboard = () => {
  // Which home screen this business gets depends on config that loads AFTER the
  // first render. Subscribing alone loses the race when the config arrives
  // first, so we also compare versions on mount — otherwise a kirana shop sits
  // on the fashion homepage until it navigates somewhere else.
  const [version, setVersion] = React.useState(configVersion);
  React.useEffect(() => {
    if (configVersion() !== version) setVersion(configVersion());
    return subscribeBusinessConfig(setVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return getDashboardWidgets().length > 0 ? <ErpDashboard/> : <HomePage/>;
};
const Profile = lazy(() => import('./components/Profile'));
const Contact = lazy(() => import('./components/Contact'));
const AboutMe = lazy(() => import('./components/AboutMe'));
const Orders = lazy(() => import('./components/Orders'));
const Proposals = lazy(() => import('./components/Proposals'));
const EditProduct = lazy(() => import('./components/EditProduct'));
const TemplateSelector = lazy(() => import('./components/Subscription'));
const TemplateThemes = lazy(() => import('./pages/TemplateThemes'));
// Public, no-login full-page render of a base template — used to auto-screenshot
// gallery cover images and for visual QA. Route: /template-shot/:templateId
const TemplateShot = lazy(() => import('./pages/TemplateShot'));
const VisualTemplateEditor = lazy(() => import('./pages/VisualTemplateEditor'));
const DesignerOnboarding = lazy(() => import('./components/DesignerOnboarding'));
const AdminDesignerDashboard = lazy(() => import('./components/AdminDesignerDashboard'));
const DesignerProfile = lazy(() => import('./components/DesignerProfile'));
const Analytics = lazy(() => import('./pages/Analytics'));
const SEOSettings = lazy(() => import('./pages/SEOSettings'));
// Everything the storefront SAYS — pages on/off, contact, policies, legal text
const StoreContentPage = lazy(() => import('./pages/StoreContent'));
const CategoriesPage = lazy(() => import('./pages/Categories/CategoriesPage'));
const OrderBook = lazy(() => import('./components/orderbook/OrderBook'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const PageDashboard = lazy(() => import('./components/TemplateBuilder/PageDashboard'));
// L7: Version history browseable UI for template drafts
const VersionHistory = lazy(() => import('./pages/VersionHistory'));

// ─── The eleven WORKFLOW HUBS (UI_CONSOLIDATION_PLAN phases 1–3) ────────
// Each hub is a tabbed container that lazy-loads the pages it absorbed; the
// absorbed pages still exist as files and are still gated per TAB inside the
// container, so nobody can see a screen the old nav would have hidden. Every
// old path below is a `<Navigate>` further down — bookmarks never 404.
//
// ⚠️ `pages/InventoryHub` is the HUB. `pages/Inventory` is the existing Stock
// dashboard, which the hub's Stock tab wraps — importing the latter here is
// the mistake this comment exists to prevent.
// UI_CONSOLIDATION_PLAN 4A — the screen a merchant lands on after login.
// `/homepage` STAYS ROUTED: FirstRunCard, PluginGuard, AdminOnlyRoute, the 404
// page, MobileBottomNav, Subscription's success dialogs and the support docs all
// link to it, and it is still where fashion's product list lives.
const Today = lazy(() => import('./pages/Today'));
const InventoryHub = lazy(() => import('./pages/InventoryHub'));
const MoneyIn = lazy(() => import('./pages/MoneyIn'));
const BillingDocuments = lazy(() => import('./pages/BillingDocuments'));
const Discounts = lazy(() => import('./pages/Discounts'));
const Engagement = lazy(() => import('./pages/Engagement'));
const Merchandising = lazy(() => import('./pages/Merchandising'));
const ReportsHub = lazy(() => import('./pages/Reports'));
const OrderBoardHub = lazy(() => import('./pages/OrderBoardHub'));
// Books / Campaigns / Customers kept their module paths — those index.js files
// ARE the hubs now, so the import below is unchanged and still correct.

// ─── Daily-Ops SaaS features (phases §1–§7) ────────────────────────────
// §1 Morning Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CounterBilling = lazy(() => import('./pages/CounterBilling'));
const Books = lazy(() => import('./pages/Books'));
const Team = lazy(() => import('./pages/Team'));
const BOM = lazy(() => import('./pages/BOM'));
const ItemRecipes = lazy(() => import('./pages/ItemRecipes'));
// D2C — customer subscriptions ("subscribe & save"). Tool-gated inside the page.
const CustomerSubscriptions = lazy(() => import('./pages/Subscriptions'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Documents = lazy(() => import('./pages/Documents'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Approvals = lazy(() => import('./pages/Approvals'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Leads = lazy(() => import('./pages/Leads'));
const Appointments = lazy(() => import('./pages/Appointments'));
const VariantManager = lazy(() => import('./pages/Inventory/VariantManager'));
const StockHistory = lazy(() => import('./pages/Inventory/StockHistory'));
// §5 Customer CRM
const CustomersHub = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/Customers/CustomerDetail'));
// §2 Unified Orders
const UnifiedInbox = lazy(() => import('./pages/Orders/UnifiedInbox'));
// §3 Production
const ProductionBoard = lazy(() => import('./pages/Production/ProductionBoard'));
const KarigarList = lazy(() => import('./pages/Production/KarigarList'));
const KarigarDetail = lazy(() => import('./pages/Production/KarigarDetail'));
// §6 Billing & Payments
// Connect Payments — designer links their OWN Razorpay account (replaces KYC/Route onboarding)
const ConnectPayments = lazy(() => import('./pages/Payments/ConnectPayments'));
// Store Settings — shipping/returns/COD/welcome-coupon the storefront reads
const StoreSettings = lazy(() => import('./pages/Payments/StoreSettings'));
const Messages = lazy(() => import('./pages/Messages'));
// Catalog Settings — categories & sizes, fabric care, upload defaults
const CatalogSettings = lazy(() => import('./pages/Catalog/CatalogSettings'));
// Per-product add-ons editor
const ProductAddonsEditor = lazy(() => import('./pages/Catalog/ProductAddonsEditor'));
const ExpenseList = lazy(() => import('./pages/Expenses/ExpenseList'));
const SupplierLedger = lazy(() => import('./pages/Expenses/SupplierLedger'));
// Purchases / Stock-In — supplier purchase accounting (adds stock + tracks payables)
const PurchasesPage = lazy(() => import('./pages/Purchases/PurchasesPage'));
// §7 Reports — P&L / GSTR-1 / Goals / Commerce are tabs of ReportsHub above.
// Comparisons keeps its own route: it has no nav entry and no hub tab.
const ReportsComparisons = lazy(() => import('./pages/Reports/Comparisons'));
// WhatsApp — setup form + messages inbox, unified
const WhatsAppHub = lazy(() => import('./pages/WhatsApp'));
const Tools = lazy(() => import('./pages/Tools'));
const Personalize = lazy(() => import('./pages/Personalize'));

// [LOW-9] Real 404 page. Previously the catch-all `*` redirected to
// /homepage (which, when unauthenticated, bounced to /login) — silently
// masking broken links and typo'd URLs. Render an explicit Not Found page
// instead so unknown routes are visible rather than disguised as a redirect.
function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center h-screen text-center px-4">
      <h1 className="text-6xl font-extrabold text-[#B40A0A]">404</h1>
      <p className="mt-4 text-lg font-semibold text-gray-900">Page not found</p>
      <p className="mt-2 text-sm text-gray-500">
        The page you’re looking for doesn’t exist or may have moved.
      </p>
      <div className="mt-6 flex gap-3">
        <Link
          to="/homepage"
          className="px-4 py-2 rounded-lg text-white font-semibold"
          style={{ background: '#B40A0A' }}
        >
          Go to Dashboard
        </Link>
        <Link
          to="/"
          className="px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50"
        >
          Back Home
        </Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <Router>
      <AuthProvider>
        <PluginProvider>
        <SidebarBadgesProvider>
        {/* Route-level boundary: a render error in any lazy route is isolated
            here so the app shell (Router + providers) survives instead of the
            single root boundary blanking the whole app. */}
        <ErrorBoundary>
        <ImpersonationBanner />
        <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          {/* SEO landing pages — one route per entry in pages/seo/seoPages.js.
              These paths MUST stay in step with apex_seo.ROUTES on the server,
              which supplies each one's title/description/canonical and lists it
              in sitemap.xml. A path in the sitemap with no route here would
              invite Google to crawl a page that renders the catch-all. */}
          {SEO_PATHS.map((p) => (
            <Route key={p} path={p} element={<SeoLandingPage path={p} />} />
          ))}
          <Route path="/old-landing" element={<LandingPage />} />
          <Route path="/contactus" element={<ContactUs />} />
          <Route path="/login" element={<Login />} />
          <Route path="/termsNconditions" element={<TermsAndServices/>} />
          <Route path="/payShip" element={<PaymentShipping/>} />
          <Route path="/rtnPolicy" element={<ReturnPolicy/>} />
          <Route path="/privacyPolicy" element={<PrivacyPolicy/>} />
          <Route path="/Faqs" element={<Faqs/>} />
          <Route path="/forgot-password" element={<ForgotPassword/>} />
          <Route path="/reset-password/:token" element={<ResetPassword/>} />
          <Route path="/template-shot/:templateId" element={<TemplateShot />} />
          {/* The post-login landing (UI_CONSOLIDATION_PLAN 4A). One screen
              answering "what needs me right now", each card deep-linking into
              the hub tab that can act on it. */}
          <Route
            path="/today"
            element={
              <PrivateRoute>
                <Today />
              </PrivateRoute>
            }
          />
          <Route
            path="/homepage"
            element={
              <PrivateRoute>
                <HomeDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/UploadProduct"
            element={
              <PrivateRoute>
                <CatalogEntry/>
              </PrivateRoute>
            }
          />
          {/* The catalog LIST for schema-driven businesses, and the edit form
              its rows open. Fashion's list lives on the dashboard and its edit
              form is /EditProduct/:productId, unchanged. */}
          <Route
            path="/catalog"
            element={
              <PrivateRoute>
                <SchemaCatalog/>
              </PrivateRoute>
            }
          />
          <Route
            path="/catalog/:itemId/edit"
            element={
              <PrivateRoute>
                <SchemaProductForm/>
              </PrivateRoute>
            }
          />
          <Route
            path="/EditProduct/:productId"
            element={
              <PrivateRoute>
                <EditProduct/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Base"
            element={
              <PrivateRoute>
                <Base/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Profile"
            element={
              <PrivateRoute>
                <Profile/>
              </PrivateRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <PrivateRoute>
                <CategoriesPage/>
              </PrivateRoute>
            }
          />
          <Route
            path="/TemplateThemes"
            element={
              <PrivateRoute>
                {/* Choosing or creating a template is the admin's. */}
                <AdminOnlyRoute what="your store's design">
                  <TemplateThemes/>
                </AdminOnlyRoute>
              </PrivateRoute>
            }
          />
          {/* The designer's way into their own editor. Themes used to be the
              only door and is now admin-only. */}
          {/* The GENERAL door: /design renders the dashboard directly (it
              resolves the store's own template internally), so nobody is sent
              to a technical /template-builder/<number> URL any more. The
              numbered route below stays for old links and admin flows. */}
          <Route
            path="/design"
            element={
              <PrivateRoute>
                <PageDashboard/>
              </PrivateRoute>
            }
          />
          <Route
            path="/visual-editor"
            element={
              <PrivateRoute>
                <VisualTemplateEditor/>
              </PrivateRoute>
            }
          />
          <Route
            path="/template/:templateId/versions"
            element={
              <PrivateRoute>
                <VersionHistory/>
              </PrivateRoute>
            }
          />
          <Route
            path="/template-builder/:templateId"
            element={
              <PrivateRoute>
                <PageDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/Contact"
            element={
              <PrivateRoute>
                <Contact/>
              </PrivateRoute>
            }
          />
          <Route
            path="/AboutMe"
            element={
              <PrivateRoute>
                <AboutMe/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Orders"
            element={
              <PrivateRoute>
                <Orders/>
              </PrivateRoute>
            }
          />
          <Route
            path="/orderbook"
            element={
              <PrivateRoute>
                <OrderBook/>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Analytics/>
              </PrivateRoute>
            }
          />
          <Route
            path="/seo-settings"
            element={
              <PrivateRoute>
                <SEOSettings/>
              </PrivateRoute>
            }
          />
          <Route
            path="/store-content"
            element={
              <PrivateRoute>
                <StoreContentPage/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Proposals"
            element={
              <PrivateRoute>
                <PluginGuard pluginKey="bid_requests">
                  <Proposals/>
                </PluginGuard>
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <PrivateRoute>
                <TemplateSelector />
              </PrivateRoute>
            }
          />
          <Route
            path="/inikru-admin"
            element={
              <AdminRoute>
                <AdminDashboard/>
              </AdminRoute>
            }
          />
          <Route
            path="/designer-onboarding"
            element={
              <PrivateRoute>
                <DesignerOnboarding/>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin-designers"
            element={
              <AdminRoute>
                <AdminDesignerDashboard/>
              </AdminRoute>
            }
          />
          <Route
            path="/designer-profile"
            element={
              <PrivateRoute>
                <DesignerProfile/>
              </PrivateRoute>
            }
          />
          <Route path="/Register" element={<SignupJourney/>}/>
          <Route path="/register-classic" element={<Signup/>}/>

          {/* ─── Plugin-gated feature routes — one plugin per sidebar item ─── */}
          <Route path="/dashboard" element={<PrivateRoute><PluginGuard pluginKey="daily_ops"><Dashboard/></PluginGuard></PrivateRoute>} />

          {/* ═══ THE ELEVEN WORKFLOW HUBS ═══════════════════════════════════
              One destination per workflow, each a tabbed container over the
              pages it absorbed.

              🔴 The hubs carry NO PluginGuard on purpose. Gating is per TAB,
              copied verbatim from the NavItem gate each tab replaced. A guard
              out here would answer ONE question for a page that asks several:
              /inventory was `pluginKey="inventory"`, which would have shown a
              wholesaler with multi-location + stock-take an "install the
              Inventory plugin" CTA instead of the two tabs they qualify for;
              /customers was `pluginKey="customers"`, which would have done the
              same to a B2B shop that only has b2b_pricing / price_lists. Each
              container renders its own "not switched on" state (linking to
              /tools) when it can show nothing. */}
          <Route path="/inventory" element={<PrivateRoute><InventoryHub/></PrivateRoute>} />
          <Route path="/money" element={<PrivateRoute><MoneyIn/></PrivateRoute>} />
          <Route path="/books" element={<PrivateRoute><Books/></PrivateRoute>} />
          <Route path="/billing-documents" element={<PrivateRoute><BillingDocuments/></PrivateRoute>} />
          <Route path="/discounts" element={<PrivateRoute><Discounts/></PrivateRoute>} />
          <Route path="/customers" element={<PrivateRoute><CustomersHub/></PrivateRoute>} />
          <Route path="/engagement" element={<PrivateRoute><Engagement/></PrivateRoute>} />
          <Route path="/campaigns" element={<PrivateRoute><Campaigns/></PrivateRoute>} />
          <Route path="/merchandising" element={<PrivateRoute><Merchandising/></PrivateRoute>} />
          {/* EXACT path — NEVER `/reports/*`. A wildcard here would swallow
              /reports/comparisons and every /reports/… redirect below. */}
          <Route path="/reports" element={<PrivateRoute><ReportsHub/></PrivateRoute>} />
          <Route path="/order-board" element={<PrivateRoute><OrderBoardHub/></PrivateRoute>} />

          {/* Records, not tabs — these keep their own plugin gate and out-rank
              the hub paths above (React Router ranks by specificity). */}
          <Route path="/customers/:id" element={<PrivateRoute><PluginGuard pluginKey="customers"><CustomerDetail/></PluginGuard></PrivateRoute>} />
          <Route path="/inventory/product/:productId" element={<PrivateRoute><PluginGuard pluginKey="inventory"><VariantManager/></PluginGuard></PrivateRoute>} />
          <Route path="/inventory/variants/:variantId/history" element={<PrivateRoute><PluginGuard pluginKey="inventory"><StockHistory/></PluginGuard></PrivateRoute>} />

          {/* ═══ OLD PATHS → hub + tab ══════════════════════════════════════
              Every route these replace still renders — as a tab inside its
              hub — and every component file is untouched. Bookmarks, support
              docs, PluginGuard links and FirstRunCard links keep working.
              Each redirect stays INSIDE PrivateRoute so an unauthenticated hit
              still bounces straight to /login exactly as it did before, rather
              than taking a detour through the hub to arrive at the same place. */}
          <Route path="/batches" element={<PrivateRoute><Navigate to="/inventory?tab=batches" replace /></PrivateRoute>} />
          <Route path="/serials" element={<PrivateRoute><Navigate to="/inventory?tab=serials" replace /></PrivateRoute>} />
          <Route path="/locations" element={<PrivateRoute><Navigate to="/inventory?tab=locations" replace /></PrivateRoute>} />
          <Route path="/stock-take" element={<PrivateRoute><Navigate to="/inventory?tab=stocktake" replace /></PrivateRoute>} />
          <Route path="/reports/stock-ageing" element={<PrivateRoute><Navigate to="/inventory?tab=ageing" replace /></PrivateRoute>} />
          {/* Barcode labels are an ACTION on the hub now, not a destination. */}
          <Route path="/labels" element={<PrivateRoute><Navigate to="/inventory?labels=1" replace /></PrivateRoute>} />

          <Route path="/payments" element={<PrivateRoute><Navigate to="/money?tab=payments" replace /></PrivateRoute>} />
          <Route path="/khata" element={<PrivateRoute><Navigate to="/money?tab=khata" replace /></PrivateRoute>} />
          <Route path="/earnings" element={<PrivateRoute><Navigate to="/money?tab=earnings" replace /></PrivateRoute>} />
          {/* Reminders stopped being a destination: "Remind" is a row action on
              the Payments and Khata rows. */}
          <Route path="/reminders" element={<PrivateRoute><Navigate to="/money?tab=payments" replace /></PrivateRoute>} />

          <Route path="/daybook" element={<PrivateRoute><Navigate to="/books?tab=daybook" replace /></PrivateRoute>} />
          <Route path="/bank-reconciliation" element={<PrivateRoute><Navigate to="/books?tab=bankrec" replace /></PrivateRoute>} />
          <Route path="/compliance" element={<PrivateRoute><Navigate to="/books?tab=compliance" replace /></PrivateRoute>} />

          <Route path="/estimates" element={<PrivateRoute><Navigate to="/billing-documents?tab=estimates" replace /></PrivateRoute>} />
          <Route path="/challans" element={<PrivateRoute><Navigate to="/billing-documents?tab=challans" replace /></PrivateRoute>} />
          <Route path="/credit-notes" element={<PrivateRoute><Navigate to="/billing-documents?tab=credit-notes" replace /></PrivateRoute>} />

          <Route path="/my-coupons" element={<PrivateRoute><Navigate to="/discounts?tab=coupons" replace /></PrivateRoute>} />
          <Route path="/sales" element={<PrivateRoute><Navigate to="/discounts?tab=sales" replace /></PrivateRoute>} />

          <Route path="/customer-segments" element={<PrivateRoute><Navigate to="/customers?tab=segments" replace /></PrivateRoute>} />
          <Route path="/b2b-buyers" element={<PrivateRoute><Navigate to="/customers?tab=b2b" replace /></PrivateRoute>} />
          <Route path="/price-lists" element={<PrivateRoute><Navigate to="/customers?tab=price-lists" replace /></PrivateRoute>} />

          <Route path="/loyalty" element={<PrivateRoute><Navigate to="/engagement?tab=loyalty" replace /></PrivateRoute>} />
          <Route path="/vouchers" element={<PrivateRoute><Navigate to="/engagement?tab=vouchers" replace /></PrivateRoute>} />
          <Route path="/referrals" element={<PrivateRoute><Navigate to="/engagement?tab=referrals" replace /></PrivateRoute>} />

          <Route path="/email-marketing" element={<PrivateRoute><Navigate to="/campaigns?tab=email" replace /></PrivateRoute>} />
          <Route path="/automations" element={<PrivateRoute><Navigate to="/campaigns?tab=automations" replace /></PrivateRoute>} />

          <Route path="/collections-manager" element={<PrivateRoute><Navigate to="/merchandising?tab=collections" replace /></PrivateRoute>} />
          <Route path="/shop-gallery" element={<PrivateRoute><Navigate to="/merchandising?tab=gallery" replace /></PrivateRoute>} />
          <Route path="/quizzes" element={<PrivateRoute><Navigate to="/merchandising?tab=finders" replace /></PrivateRoute>} />

          <Route path="/reports/pnl" element={<PrivateRoute><Navigate to="/reports?tab=pnl" replace /></PrivateRoute>} />
          <Route path="/reports/gstr1" element={<PrivateRoute><Navigate to="/reports?tab=gstr1" replace /></PrivateRoute>} />
          <Route path="/reports/goals" element={<PrivateRoute><Navigate to="/reports?tab=goals" replace /></PrivateRoute>} />
          <Route path="/reports/commerce" element={<PrivateRoute><Navigate to="/reports?tab=commerce" replace /></PrivateRoute>} />

          <Route path="/preorders" element={<PrivateRoute><Navigate to="/order-board?tab=preorders" replace /></PrivateRoute>} />

          {/* ─── Screens that are still their own destination ─── */}
          {/* A3 — Counter Billing (POS). Capability-gated inside the page. */}
          <Route path="/counter" element={<PrivateRoute><CounterBilling/></PrivateRoute>} />
          {/* A8 — Recipes & yield (BOM). Capability-gated inside the page. */}
          <Route path="/recipes" element={<PrivateRoute><BOM/></PrivateRoute>} />
          {/* P2.4 — Item recipes (café BOM). Capability-gated inside the page. */}
          <Route path="/item-recipes" element={<PrivateRoute><ItemRecipes/></PrivateRoute>} />
          {/* D2C — customer subscriptions. Tool-gated inside the page. */}
          <Route path="/subscriptions" element={<PrivateRoute><CustomerSubscriptions/></PrivateRoute>} />
          {/* Document Locker — owner/manager only (gated inside the page). */}
          <Route path="/documents" element={<PrivateRoute><Documents/></PrivateRoute>} />
          {/* Portfolio / public link-hub management. */}
          <Route path="/portfolio" element={<PrivateRoute><Portfolio/></PrivateRoute>} />
          {/* Wave 2 — approvals + B2B quotations + leads. Capability-gated inside. */}
          <Route path="/approvals" element={<PrivateRoute><Approvals/></PrivateRoute>} />
          <Route path="/quotes" element={<PrivateRoute><Quotes/></PrivateRoute>} />
          <Route path="/leads" element={<PrivateRoute><Leads/></PrivateRoute>} />
          <Route path="/appointments" element={<PrivateRoute><Appointments/></PrivateRoute>} />
          <Route
            path="/returns"
            element={
              <PrivateRoute>
                <ReturnsPage />
              </PrivateRoute>
            }
          />
          <Route path="/orders-unified" element={<PrivateRoute><PluginGuard pluginKey="unified_inbox"><UnifiedInbox/></PluginGuard></PrivateRoute>} />
          <Route path="/production" element={<PrivateRoute><PluginGuard pluginKey="production_board"><ProductionBoard/></PluginGuard></PrivateRoute>} />
          <Route path="/karigars" element={<PrivateRoute><PluginGuard pluginKey="team"><KarigarList/></PluginGuard></PrivateRoute>} />
          <Route path="/karigars/:id" element={<PrivateRoute><PluginGuard pluginKey="team"><KarigarDetail/></PluginGuard></PrivateRoute>} />
          <Route path="/connect-payments" element={<PrivateRoute><ConnectPayments/></PrivateRoute>} />
          {/* Team & Roles — owner-only staff logins. Gated inside the page via isOwner(). */}
          <Route path="/team" element={<PrivateRoute><Team/></PrivateRoute>} />
          <Route path="/store-settings" element={<PrivateRoute><StoreSettings/></PrivateRoute>} />
          <Route path="/messages" element={<PrivateRoute><Messages/></PrivateRoute>} />
          <Route path="/catalog-settings" element={<PrivateRoute><CatalogSettings/></PrivateRoute>} />
          <Route path="/product-addons/:productId" element={<PrivateRoute><ProductAddonsEditor/></PrivateRoute>} />
          <Route path="/expenses" element={<PrivateRoute><PluginGuard pluginKey="expenses"><ExpenseList/></PluginGuard></PrivateRoute>} />
          <Route path="/suppliers" element={<PrivateRoute><PluginGuard pluginKey="suppliers"><SupplierLedger/></PluginGuard></PrivateRoute>} />
          <Route path="/suppliers/:id" element={<PrivateRoute><PluginGuard pluginKey="suppliers"><SupplierLedger/></PluginGuard></PrivateRoute>} />
          <Route path="/purchases" element={<PrivateRoute><PurchasesPage/></PrivateRoute>} />
          {/* Comparisons has no nav entry and no hub tab — its own route stays. */}
          <Route path="/reports/comparisons" element={<PrivateRoute><ReportsComparisons/></PrivateRoute>} />
          <Route path="/whatsapp" element={<PrivateRoute><PluginGuard pluginKey="whatsapp"><WhatsAppHub/></PluginGuard></PrivateRoute>} />

          {/* Plugin Store — always available */}
          {/* Tools is the merchant's PERMANENT feature switchboard and is no
              longer beta-gated: /setup is cleared after the first run and
              /plugins redirects here, so gating this route left an owner with
              no way to switch anything on ever again (see canManageFeatures).
              The page itself refuses staff, matching the server.
              Personalize is genuinely unfinished and stays behind the flag. */}
          <Route path="/tools" element={<PrivateRoute><Tools/></PrivateRoute>} />
          <Route path="/personalize" element={<PrivateRoute>{canUseTools() ? <Personalize/> : <Navigate to="/homepage" replace/>}</PrivateRoute>} />
          {/* The Plugin Store is superseded by Tools (which knows about every
              switch, not just the plugin half). Old links keep working. */}
          <Route path="/plugins" element={<Navigate to="/tools" replace />} />
          <Route path="/setup" element={<PrivateRoute><Setup/></PrivateRoute>} />

          {/* [LOW-9] Real 404 instead of a silent redirect to /homepage. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        </SidebarBadgesProvider>
        </PluginProvider>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;